import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs } from "../../api/examApi";
import { getAdmitCardInstruction, saveAdmitCardInstruction } from "../../api/admitCardInstructionApi";
import "../Attendance/Attendance.css";

const DEFAULTS = [
  "Examinee must enter into the exam hall at least 15 minutes before the exam start.",
  "No extra papers will be allowed except the admit.",
  "Examinee must carry all necessary essentials for the exam.",
  "Authority deserves all rights to expel the examinee due to unfair-means in the exam.",
];

/**
 * Direct port of digicampusbd's `admit-card-instruction` ("Instruction
 * Setup") — up to 4 free-text instruction lines saved per exam configuration,
 * later printed on that exam's admit cards (see `AdmitCardPage`). Uses the
 * new `GET /api/admit-card-instructions` to pre-fill existing instructions
 * when an exam is selected — a small, deliberate fix over the old app, whose
 * own page never loaded existing values at all (its `examconfigid` change
 * handler only reset 4 hardcoded default placeholder strings, confirmed by
 * reading admit-card-instruction.html directly).
 */
export default function AdmitCardInstructionPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");

  const [instructions, setInstructions] = useState(["", "", "", ""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    setSuccess("");
    setError("");
    if (!examConfigId) {
      setInstructions(["", "", "", ""]);
      return;
    }
    getAdmitCardInstruction(examConfigId)
      .then((existing) => {
        if (existing) {
          setInstructions([existing.instructionOne || "", existing.instructionTwo || "", existing.instructionThree || "", existing.instructionFour || ""]);
        } else {
          setInstructions(DEFAULTS);
        }
      })
      .catch(() => setInstructions(DEFAULTS));
  }, [examConfigId]);

  const updateInstruction = (index, value) => {
    setInstructions((prev) => prev.map((v, i) => (i === index ? value : v)));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!examConfigId) {
      setError("Please select an exam.");
      return;
    }
    if (instructions.every((v) => !v.trim())) {
      setError("Please add at least one instruction.");
      return;
    }
    setSaving(true);
    try {
      await saveAdmitCardInstruction({
        examConfigId,
        instructionOne: instructions[0],
        instructionTwo: instructions[1],
        instructionThree: instructions[2],
        instructionFour: instructions[3],
      });
      setSuccess("Instructions saved.");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save instructions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Instruction Setup</h1>
      </div>

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      <div className="attendance-filters">
        <label>
          Select Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
      </div>

      <form className="user-form" onSubmit={handleSave} style={{ marginTop: 16 }}>
        {instructions.map((value, i) => (
          <label key={i} style={{ display: "block", marginBottom: 12 }}>
            {`Instruction-${i + 1}`}
            <textarea rows={3} style={{ width: "100%" }} value={value} onChange={(e) => updateInstruction(i, e.target.value)} />
          </label>
        ))}

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving || !examConfigId}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </MainLayout>
  );
}
